import type {
	FinancialPropertyListItem,
	ServicePeriodListItem,
} from "@/features/financialShared";
import {
	ServicePeriodStatus,
	toApiSortOrder,
} from "@/features/financialShared";
import type {
	GetRevenueManagementPropertiesArgs,
	GetRevenueServicePeriodsArgs,
	RevenueApproverOption,
	RevenueManagementPermissions,
	RevenueManagementPropertiesResponse,
	RevenueServicePeriodsResponse,
} from "./revenueManagement.api.types";
import { mapRevenueStatusFromApi } from "./revenueManagement.api.types";

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return value as Record<string, unknown>;
}

function asString(value: unknown, fallback = ""): string {
	return typeof value === "string" ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
	if (typeof value === "boolean") return value;
	return fallback;
}

function unwrapData(raw: unknown): Record<string, unknown> {
	const root = asRecord(raw) ?? {};
	return asRecord(root.data) ?? root;
}

function formatShortDate(value: string): string {
	const trimmed = value.trim();
	if (!trimmed) return "";
	const date = new Date(trimmed);
	if (Number.isNaN(date.getTime())) return trimmed;
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function parseApproverOptions(raw: unknown): RevenueApproverOption[] {
	if (!Array.isArray(raw)) return [];
	return raw
		.map((item) => {
			const row = asRecord(item);
			if (!row) return null;
			const id = asString(row.id).trim();
			const name = asString(row.name).trim();
			if (!id && !name) return null;
			return { id: id || name, name: name || id };
		})
		.filter((item): item is RevenueApproverOption => Boolean(item));
}

function parseApproverName(raw: unknown): string {
	if (typeof raw === "string") return raw.trim();
	const row = asRecord(raw);
	if (!row) return "";
	return asString(row.name).trim();
}

function parseLatestPeriodLabel(raw: unknown): string {
	if (!raw) return "—";
	if (typeof raw === "string") return raw.trim() || "—";
	const row = asRecord(raw);
	if (!row) return "—";
	return asString(row.label).trim() || "—";
}

function parsePropertyItem(raw: unknown): FinancialPropertyListItem | null {
	const row = asRecord(raw);
	if (!row) return null;
	const id = asString(row.propertyId ?? row.id).trim();
	const name = asString(row.propertyName ?? row.name).trim();
	if (!id) return null;
	return {
		id,
		name: name || "Untitled property",
		totalUnits: asNumber(row.totalUnits),
		latestServicePeriodLabel: parseLatestPeriodLabel(row.latestServicePeriod),
		status: mapRevenueStatusFromApi(row.status),
		lastUpdatedDate:
			asString(row.lastUpdatedDisplay).trim() ||
			formatShortDate(asString(row.lastUpdatedDate)) ||
			"—",
		approver: parseApproverName(row.approver) || "—",
	};
}

export function parseRevenueManagementPropertiesResponse(
	raw: unknown,
): RevenueManagementPropertiesResponse {
	const data = unwrapData(raw);
	const items = Array.isArray(data.items)
		? data.items
				.map(parsePropertyItem)
				.filter((item): item is FinancialPropertyListItem => Boolean(item))
		: [];
	const page = Math.max(1, asNumber(data.page, 1));
	const pageSize = Math.max(1, asNumber(data.pageSize, 10));
	const total = asNumber(data.total, items.length);
	const totalPages = Math.max(1, asNumber(data.totalPages, 1));
	const lastSyncDisplay = asString(data.lastSyncDisplay).trim();
	const permissions = asRecord(data.permissions) ?? {};
	return {
		pageTitle: asString(data.pageTitle, "Revenue Management"),
		lastSyncLabel: lastSyncDisplay ? `Last Sync: ${lastSyncDisplay}` : "",
		approverOptions: parseApproverOptions(data.approverOptions),
		items,
		page,
		pageSize,
		total,
		totalPages,
		hasPrevious: asBoolean(data.hasPrevious, page > 1),
		hasNext: asBoolean(data.hasNext, page < totalPages),
		permissions: {
			canEdit: asBoolean(permissions.canEdit, true),
			canPublish: asBoolean(permissions.canPublish, false),
		} satisfies RevenueManagementPermissions,
	};
}

function parsePeriodItem(
	raw: unknown,
	propertyId: string,
): ServicePeriodListItem | null {
	const row = asRecord(raw);
	if (!row) return null;
	const id = asString(row.id).trim();
	if (!id) return null;
	const paidOut = asNumber(row.paidOutUnits);
	const totalUnits = asNumber(row.totalUnits);
	const approverName = parseApproverName(row.approver);
	const label =
		asString(row.label).trim() ||
		[asString(row.periodStart), asString(row.periodEnd)]
			.filter(Boolean)
			.join(" – ") ||
		"Untitled period";
	return {
		id,
		propertyId: asString(row.propertyId, propertyId),
		periodLabel: label,
		paidUnitsLabel: `${paidOut}/${totalUnits}`,
		status: mapRevenueStatusFromApi(row.status) ?? ServicePeriodStatus.DRAFT,
		createdDate:
			formatShortDate(asString(row.createdAt ?? row.createdDate)) || "—",
		lastUpdatedBy: approverName || "—",
		lastUpdatedDate:
			formatShortDate(asString(row.updatedAt ?? row.lastUpdatedDate)) || "—",
		approver: approverName || "—",
		startDate: formatShortDate(asString(row.periodStart)),
		endDate: formatShortDate(asString(row.periodEnd)),
		isUploaded: true,
	};
}

export function parseRevenueServicePeriodsResponse(
	raw: unknown,
	propertyId: string,
): RevenueServicePeriodsResponse {
	const data = unwrapData(raw);
	const resolvedPropertyId = asString(data.propertyId, propertyId);
	const items = Array.isArray(data.items)
		? data.items
				.map((item) => parsePeriodItem(item, resolvedPropertyId))
				.filter((item): item is ServicePeriodListItem => Boolean(item))
		: [];
	return {
		propertyId: resolvedPropertyId,
		propertyName: asString(data.propertyName).trim(),
		unitCount: asNumber(data.unitCount),
		items,
		approverOptions: parseApproverOptions(data.approverOptions),
		page: Math.max(1, asNumber(data.page, 1)),
		pageSize: Math.max(1, asNumber(data.pageSize, 10)),
		total: asNumber(data.total, items.length),
		totalPages: Math.max(1, asNumber(data.totalPages, 1)),
		emptyMessage:
			asString(data.emptyMessage).trim() || "No service periods found.",
	};
}

export function buildRevenueManagementListParams(
	args: GetRevenueManagementPropertiesArgs,
): Record<string, string | number> {
	const params: Record<string, string | number> = {
		status: args.status?.trim() || "ALL",
		sortBy: args.sortBy?.trim() || "NAME",
		sortOrder: toApiSortOrder(args.sortOrder),
		page: args.page ?? 1,
		pageSize: args.pageSize ?? 10,
	};
	if (args.search?.trim()) params.search = args.search.trim();
	if (args.approverId?.trim()) params.approverId = args.approverId.trim();
	return params;
}

export function buildRevenueServicePeriodListParams(
	args: GetRevenueServicePeriodsArgs,
): Record<string, string | number> {
	const params: Record<string, string | number> = {
		page: args.page ?? 1,
		pageSize: args.pageSize ?? 10,
	};
	if (args.search?.trim()) params.search = args.search.trim();
	if (args.status?.trim() && args.status !== "ALL") {
		params.status = args.status.trim();
	}
	if (args.approverId?.trim()) params.approverId = args.approverId.trim();
	return params;
}

export {
	parseDownloadedFilename,
	triggerBrowserDownload,
} from "@/features/financialShared";
